import React, { useCallback, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { Input, Select, Text, Button } from "@geist-ui/core";
import { useTheme } from "@geist-ui/core";
import CountryUtils from "../../../../utility/country-utils";
import { EDUCATION, PROFESSION } from "../../../../types/investment_types";
import {
  LEGAL_ENTITY_TYPE,
  SALUTATION,
  TITLE,
} from "../../../../types/inventure-user_types";
import { DOCUMENT_TYPE } from "../../../../types/onboarding_types";
import { useDropzone } from "react-dropzone";

// --- STYLES ---
// Custom styles to override Geist UI default look for a "Trade Republic"-like aesthetic
const inputContainerStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
  width: '100%',
  position: 'relative'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.5rem',
  color: '#444', // Darker label for less shyness
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontFamily: '"Nunito Sans", sans-serif',
  marginLeft: '4px',
  transition: 'color 0.2s ease'
};

// "Snappy" organic transition curve
const snappyTransition = 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)';

const inputBaseStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF', // White background for better contrast against gray pages
    borderRadius: '16px',
    border: '1px solid #D4D4D4', // Much darker, visible border
    height: '56px',
    fontSize: '16px',
    color: '#000',
    width: '100%',
    padding: '0 20px',
    transition: snappyTransition,
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)', 
    transform: 'scale(1)',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
};

const inputFocusStyle: React.CSSProperties = {
    borderColor: '#000',
    borderWidth: '1px', // Keep 1px but black
    backgroundColor: '#fff',
    boxShadow: '0 8px 20px rgba(0,0,0,0.08)', // Stronger lift
    transform: 'scale(1.005) translateY(-2px)', // Stronger pop
};

/* 
   Helper to merge styles for focus handling since we use inline styles for the "organic" feel 
   that might be hard to achieve with standard classes without Tailwind setup
*/
const getFocusHandlers = (theme: any, error?: boolean) => ({
    onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
        Object.assign(e.currentTarget.style, inputFocusStyle);
        // Find label and darken it
        const label = e.currentTarget.parentElement?.parentElement?.querySelector('label');
        if (label) label.style.color = '#000';
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
        e.currentTarget.style.borderColor = error ? theme.palette.error : '#D4D4D4'; // Return to darker border
        e.currentTarget.style.borderWidth = '1px';
        e.currentTarget.style.backgroundColor = '#FFFFFF';
        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
        e.currentTarget.style.transform = 'scale(1) translateY(0)';
        
        const label = e.currentTarget.parentElement?.parentElement?.querySelector('label');
        if (label) label.style.color = '#444';
    }
});

interface Picker {
  name: string;
  error?: boolean;
  control: Control<any>;
  label: string;
  required?: boolean;
  tabIndex?: number;
  disabled?: boolean;
  placeholder?: string;
  width?: string;
}

interface HookFormTextFieldProps extends Picker {
  defaultValue?: string | null;
  type?: string;
}

interface HookFormSelectProps extends Picker {
    defaultValue?: any;
}

export function GeistHookFormInput({
  name,
  defaultValue,
  error,
  control,
  label,
  required,
  type = "text",
  width = "100%",
  placeholder,
  disabled
}: HookFormTextFieldProps) {
  const theme = useTheme();
  
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      rules={{ required: required }}
      control={control}
      render={({ field: { onChange, value } }) => (
        <div style={{ ...inputContainerStyle, width }}>
          <label style={labelStyle}>
             {label}
          </label>
          <div className={`custom-input-wrapper ${error ? 'error' : ''}`}>
             <input
                className="w-full px-4 py-3 bg-[#fafafa] rounded-xl border border-transparent focus:border-black focus:bg-white focus:ring-0 transition-all outline-none text-[16px] placeholder-gray-400 font-medium"
                style={{
                    ...inputBaseStyle,
                    border: `1px solid ${error ? theme.palette.error : '#EAEAEA'}`,
                    height: '52px'
                }}
                {...getFocusHandlers(theme, error)}
                value={value || ""}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                type={type}
             />
          </div>
          {error && (
              <span style={{ color: theme.palette.error, fontSize: '12px', marginTop: '4px', display: 'block', marginLeft: '4px' }}>
                  Bitte dieses Feld ausfüllen
              </span>
          )}
        </div>
      )}
    />
  );
}

export function GeistHookFormSelect({
    name,
    defaultValue,
    error,
    control,
    label,
    required,
    width = "100%",
    children
  }: HookFormSelectProps & { children: React.ReactNode }) {
    
    // For Select, standard HTML Select is hard to style perfectly. 
    // We will use Geist Select but wrapped/styled to match or a custom implementation?
    // Geist Select is complex. Let's try to style Geist Select to match our inputs.
    // NOTE: Geist Select has strict styling. 
    // Alternative: Use a standard <select> with appearance-none for consistent "box" look.

    const theme = useTheme();

    return (
      <Controller
        name={name}
        defaultValue={defaultValue}
        rules={{ required: required }}
        control={control}
        render={({ field: { onChange, value } }) => (
          <div style={{ ...inputContainerStyle, width }}>
            <label style={labelStyle}>
               {label}
            </label>
            <div style={{ position: 'relative' }}>
                <select
                    value={value}
                    onChange={onChange}
                    style={{
                        ...inputBaseStyle,
                        appearance: 'none',
                        border: `1px solid ${error ? theme.palette.error : '#EAEAEA'}`,
                        height: '52px',
                        color: value ? '#000' : '#888',
                        paddingRight: '40px', // Space for chevron
                        cursor: 'pointer'
                    }}
                    className="w-full bg-[#fafafa] rounded-xl border border-gray-100 focus:border-black focus:bg-white transition-all outline-none font-medium"
                    {...getFocusHandlers(theme, error)}
                >
                     <option value="" disabled hidden>Bitte wählen...</option>
                     {children}
                </select>
                {/* Custom Chevron */}
                <div style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
            </div>
            {error && (
              <span style={{ color: theme.palette.error, fontSize: '12px', marginTop: '4px', display: 'block', marginLeft: '4px' }}>
                  Bitte eine Auswahl treffen
              </span>
            )}
          </div>
        )}
      />
    );
  }

export function GeistHookFormCountrySelect({
  name,
  defaultValue,
  error,
  control,
  label,
  required
}: HookFormSelectProps) {
  return (
    <GeistHookFormSelect
        name={name}
        defaultValue={defaultValue || "DEU"}
        error={error}
        control={control}
        label={label}
        required={required}
    >
         {CountryUtils.getCountries().map((country) => (
             <option key={country.alpha3} value={country.alpha3}>
                 {country.name}
             </option>
         ))}
    </GeistHookFormSelect>
  );
}

export function GeistHookFormSalutationSelect({
    name,
    defaultValue,
    error,
    control,
    label,
    required
  }: HookFormSelectProps) {
    return (
      <GeistHookFormSelect
          name={name}
          defaultValue={defaultValue || SALUTATION.MS}
          error={error}
          control={control}
          label={label}
          required={required}
      >
           <option value={SALUTATION.MS}>Frau</option>
           <option value={SALUTATION.MR}>Herr</option>
           <option value={SALUTATION.D}>Divers</option>
      </GeistHookFormSelect>
    );
  }

export function GeistHookFormTitleSelect({
    name,
    defaultValue,
    control,
    label,
  }: HookFormSelectProps) {
    return (
      <GeistHookFormSelect
          name={name}
          defaultValue={defaultValue || TITLE.NONE}
          error={false}
          control={control}
          label={label}
      >
           <option value={TITLE.NONE}>Kein Titel</option>
           <option value={TITLE.DR}>Dr.</option>
      </GeistHookFormSelect>
    );
}

export function GeistHookFormProfessionPicker({
    name,
    defaultValue,
    control,
    label,
    error,
    required
  }: HookFormSelectProps) {
    return (
        <GeistHookFormSelect
            name={name}
            defaultValue={defaultValue}
            control={control}
            label={label}
            error={error}
            required={required}
        >
             {Object.entries(PROFESSION).map(([key, value]) => (
                <option key={value} value={value}>{value as string}</option>
             ))}
        </GeistHookFormSelect>
    );
}

export function GeistHookFormEducationPicker({
    name,
    defaultValue,
    control,
    label,
    error,
    required
  }: HookFormSelectProps) {
    return (
        <GeistHookFormSelect
            name={name}
            defaultValue={defaultValue}
            control={control}
            label={label}
            error={error}
            required={required}
        >
             {Object.entries(EDUCATION).map(([key, value]) => (
                <option key={value} value={value}>{value as string}</option>
             ))}
        </GeistHookFormSelect>
    );
}

export function GeistHookFormLegitimationDocumentPicker({
    name,
    defaultValue,
    control,
    label,
    error,
    required
  }: HookFormSelectProps) {
    return (
        <GeistHookFormSelect
            name={name}
            defaultValue={defaultValue}
            control={control}
            label={label}
            error={error}
            required={required}
        >
            <option value={DOCUMENT_TYPE.IDCARD.toString()}>Personalausweis</option>
            <option value={DOCUMENT_TYPE.PASSPORT.toString()}>Reisepass</option>
        </GeistHookFormSelect>
    );
}

export function GeistHookFormCompanyTypeSelect({
    name,
    defaultValue,
    control,
    label,
    error,
    required
  }: HookFormSelectProps) {
    return (
        <GeistHookFormSelect
            name={name}
            defaultValue={defaultValue}
            control={control}
            label={label}
            error={error}
            required={required}
        >
            <option value={LEGAL_ENTITY_TYPE.GMBH}>GmbH</option>
            <option value={LEGAL_ENTITY_TYPE.UG}>UG</option>
        </GeistHookFormSelect>
    );
}


export function GeistHookFormDatePicker({
    name,
    defaultValue,
    error,
    control,
    label,
    required
  }: HookFormTextFieldProps) {
     const theme = useTheme();
     
     return (
        <Controller
            name={name}
            defaultValue={defaultValue}
            rules={{ required: required }}
            control={control}
            render={({ field: { onChange, value } }) => (
                 <div style={{ ...inputContainerStyle }}>
                    <label style={labelStyle}>
                        {label}
                    </label>
                    <input
                        className="w-full px-4 py-3 bg-[#fafafa] rounded-xl border border-gray-100 focus:border-black focus:bg-white focus:ring-0 transition-all outline-none text-[16px] placeholder-gray-400 font-medium"
                        style={{
                            ...inputBaseStyle,
                            appearance: 'none',
                            border: `1px solid ${error ? theme.palette.error : '#EAEAEA'}`,
                            height: '52px'
                        }}
                        {...getFocusHandlers(theme, error)}
                        value={value ? new Date(value).toISOString().split('T')[0] : ""}
                        onChange={(e) => onChange(e.target.value)}
                        type="date"
                    />
                     {error && (
                        <span style={{ color: theme.palette.error, fontSize: '12px', marginTop: '4px', display: 'block', marginLeft: '4px' }}>
                            Bitte gültiges Datum eingeben
                        </span>
                    )}
                </div>
            )}
        />
     )
  }

  export function GeistHookFormFile({ onFileDrop }: { onFileDrop: (file: File) => void }) {
    const theme = useTheme();
    const [file, setFile] = useState<File | null>(null);
  
    const onDrop = useCallback((acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
        onFileDrop(acceptedFiles[0]);
      }
    }, [onFileDrop]);
  
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      accept: 'application/pdf',
      multiple: false,
    });
  
    return (
      <div
        {...getRootProps()}
        style={{
          border: `1px dashed ${isDragActive ? '#000' : theme.palette.accents_3}`,
          borderRadius: '12px',
          padding: '2.5rem 2rem',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? '#FAFAFA' : 'transparent',
          transition: 'all 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '160px'
        }}
      >
        <input {...getInputProps()} />
        {!file ? (
          <>
            <Text p b style={{ marginBottom: '0.25rem', fontSize: '1rem' }}>Datei hier ablegen</Text>
            <Text small type="secondary" style={{ fontSize: '13px' }}>Nur .pdf Dateien erlaubt</Text>
            <div style={{ marginTop: '1.25rem' }}>
                 <Button auto scale={0.6} className="rounded-full font-bold uppercase tracking-tight">Datei auswählen</Button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                   <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                   <polyline points="14 2 14 8 20 8"></polyline>
                   <line x1="16" y1="13" x2="8" y2="13"></line>
                   <line x1="16" y1="17" x2="8" y2="17"></line>
                   <polyline points="10 9 9 9 8 9"></polyline>
               </svg>
            </div>
            <Text b style={{fontSize: '15px'}}>{file.name}</Text>
            <Text small type="success" style={{ fontWeight: 600, marginTop: '2px' }}>Bereit zum Upload</Text>
             <div style={{ marginTop: '1rem' }}>
                 <Button auto scale={0.5} type="error" ghost onClick={(e) => { e.stopPropagation(); setFile(null); }}>Entfernen</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

